"use client";

import Image from "next/image";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Clock, MapPin, Phone, Search, X } from "lucide-react";
import { memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";

import type { ReadOnlyMenuItem, ReadOnlyMenuSection } from "@/app/(public)/menu/registry-menu-page";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { MenuReserveFooter } from "@/components/menu/menu-reserve-footer";
import { MenuItemCtaFooter } from "@/components/menu/menu-item-cta-footer";
import { RomaMenuMobileCtaBar } from "@/components/menu/roma-menu-mobile-cta-bar";
import { menuSeoFaqs, menuSeoHighlights } from "@/lib/seo/content-quality";
import {
  getCenteredHorizontalScrollLeft,
  getMenuScrollSpyReadingLine,
} from "@/lib/menu-scrollspy";
import { shouldApplyRomaDarkFunnel } from "@/lib/roma-funnel-tokens";
import { cn } from "@/lib/utils";
import { useMarketingLocationContext } from "@/lib/marketing-location-context";
import { getPublicPageBrand } from "@/lib/public-brand-theme";

type PopularItem = ReadOnlyMenuItem & {
  category?: string;
};

type MenuGuideLink = {
  href: string;
  label: string;
  ariaLabel: string;
  description: string;
};

type MenuAtAGlanceItem = {
  label: string;
  detail: string;
};

export type ReadOnlyOwnerMenuCopy = {
  title?: string;
  description?: string;
  /** Short ad-matching hours callout (e.g. "Happy hour 4-6 PM") shown as a chip under the hours row. */
  hoursHighlight?: string;
  guideIntro?: string;
  menuGuideLinks?: readonly MenuGuideLink[];
  atAGlanceTitle?: string;
  atAGlanceIntro?: string;
  atAGlanceItems?: readonly MenuAtAGlanceItem[];
  emptyStateDescription?: string;
  showSeoSections?: boolean;
};

type ReadOnlyOwnerMenuProps = {
  sections: ReadOnlyMenuSection[];
  popularItems: PopularItem[];
  location: {
    label?: string;
    hours: string;
    neighborhood: string;
    phone: string;
    /** Roma-only: Henderson branch hours/phone, shown alongside the primary location. */
    secondary?: {
      label: string;
      hours: string;
      neighborhood: string;
      phone: string;
    } | null;
  };
  copy?: ReadOnlyOwnerMenuCopy;
};

const ACTIVE_CATEGORY_TUSCAN = "bg-[#f45c24] text-[#1a1a1a] border-[#f45c24]";
const INACTIVE_CATEGORY =
  "border-[#e6e6e6] bg-white text-[#4d4d4d] hover:border-[#bcbcbc] hover:text-[#0d0d0d]";

const menuGuideLinks = [
  {
    href: "/best/restaurants-southern-highlands-las-vegas",
    label: "Food places",
    ariaLabel: "Food places in Southern Highlands",
    description: "A local restaurant for food near me searches: dinner, pizza, burgers, brunch, drinks, and patio tables.",
  },
  {
    href: "/best/burgers-southern-highlands-las-vegas",
    label: "Burgers",
    ariaLabel: "Burgers in Southern Highlands",
    description: "Smash burgers, fries, and bar-food plates.",
  },
  {
    href: "/best/pizza-southern-highlands-las-vegas",
    label: "Pizza",
    ariaLabel: "Pizza in Southern Highlands",
    description: "Stone-fired pizza, classic toppings, and shareable plates.",
  },
  {
    href: "/best/brunch-southern-highlands-las-vegas",
    label: "Sunday Brunch",
    ariaLabel: "Sunday brunch in Southern Highlands",
    description: "Sunday brunch plates, drinks, and patio seating.",
  },
  {
    href: "/best/happy-hour-southern-highlands-las-vegas",
    label: "Happy Hour",
    ariaLabel: "Happy Hour in Southern Highlands",
    description: "Daily 4-6 PM drinks and bites.",
  },
  {
    href: "/best/catering-southern-highlands-las-vegas",
    label: "Catering",
    ariaLabel: "Catering in Southern Highlands",
    description: "Pizza bundles, platters, and group-event food.",
  },
] as const;

const menuAtAGlance = [
  {
    label: "Dinner",
    detail: "Starters, sharables, salads, burgers, sandwiches, mains, and bar-friendly plates.",
  },
  {
    label: "Pizza",
    detail: "Stone-fired pizza options for dine-in, game night, and group tables.",
  },
  {
    label: "Brunch",
    detail: "Sunday-only brunch with plates, sweets, sides, and drinks.",
  },
  {
    label: "Cocktails & Wine",
    detail: "Cocktails, beer, wine, champagne, and bar seating in Southern Highlands.",
  },
  {
    label: "Dessert",
    detail: "Cheesecake, gelato, and sundae options from the current printed menu.",
  },
  {
    label: "Happy hour",
    detail: "Daily 4-6 PM at the bar with the full menu nearby for dinner plans.",
  },
] as const;

type ResolvedReadOnlyOwnerMenuCopy = {
  title: string;
  description: string;
  hoursHighlight?: string;
  guideIntro: string;
  menuGuideLinks: readonly MenuGuideLink[];
  atAGlanceTitle: string;
  atAGlanceIntro: string;
  atAGlanceItems: readonly MenuAtAGlanceItem[];
  emptyStateDescription: string;
  showSeoSections: boolean;
};

const defaultMenuCopy: ResolvedReadOnlyOwnerMenuCopy = {
  title: "Tuscan Cove Bar & Patio Menu",
  description:
    "Stone-fired pizza, burgers, Sunday brunch, daily happy hour, and cocktails at our Southern Highlands location.",
  guideIntro:
    "Local guides for dinner, pizza, brunch, happy hour, burgers, and catering in Southern Highlands.",
  menuGuideLinks,
  atAGlanceTitle: "What Tuscan Cove serves",
  atAGlanceIntro:
    "A quick scan for Southern Highlands dinner, pizza, brunch, drinks, catering, and happy hour.",
  atAGlanceItems: menuAtAGlance,
  emptyStateDescription:
    "Try another search or clear the filter to browse the full Tuscan Cove menu.",
  showSeoSections: true,
};

const sectionGuideLinks: Record<string, { href: string; label: string }> = {
  "dinner-starters": {
    href: "/best/restaurants-southern-highlands-las-vegas",
    label: "Food near Southern Highlands",
  },
  "dinner-starters-and-shareables": {
    href: "/best/restaurants-southern-highlands-las-vegas",
    label: "Food near Southern Highlands",
  },
  "dinner-sharables": {
    href: "/best/restaurants-southern-highlands-las-vegas",
    label: "Shareables in Southern Highlands",
  },
  "dinner-stone-fired-pizza": {
    href: "/best/pizza-southern-highlands-las-vegas",
    label: "More pizza in Southern Highlands",
  },
  "dinner-mains": {
    href: "/best/burgers-southern-highlands-las-vegas",
    label: "More burgers in Southern Highlands",
  },
  "brunch-sunday-brunch": {
    href: "/best/brunch-southern-highlands-las-vegas",
    label: "Sunday brunch in Southern Highlands",
  },
  "brunch-eggs-and-more": {
    href: "/best/brunch-southern-highlands-las-vegas",
    label: "Sunday brunch in Southern Highlands",
  },
  "brunch-savory": {
    href: "/best/brunch-southern-highlands-las-vegas",
    label: "Brunch near Southern Highlands",
  },
  "brunch-bottomless": {
    href: "/best/brunch-southern-highlands-las-vegas",
    label: "Sunday brunch drinks in Southern Highlands",
  },
  beverages: {
    href: "/best/happy-hour-southern-highlands-las-vegas",
    label: "Daily happy hour in Southern Highlands",
  },
  drinks: {
    href: "/best/happy-hour-southern-highlands-las-vegas",
    label: "Daily happy hour in Southern Highlands",
  },
  "drinks-cocktails": {
    href: "/best/happy-hour-southern-highlands-las-vegas",
    label: "Daily happy hour in Southern Highlands",
  },
  "catering-catering-and-party-platters": {
    href: "/best/catering-southern-highlands-las-vegas",
    label: "Catering in Southern Highlands",
  },
};

function normalizeSearch(value: string) {
  return value.trim().toLowerCase();
}

function itemMatchesSearch(item: ReadOnlyMenuItem, query: string) {
  if (!query) return true;

  return [item.name, item.price, item.description, item.subcategory]
    .filter(Boolean)
    .some((value) => value!.toLowerCase().includes(query));
}

export type MenuServiceProgram = {
  policy: NonNullable<ReadOnlyMenuItem["servicePolicy"]>;
  participantNames: string[];
};

/** Group only explicitly linked menu rows; matching names must never join a program. */
export function groupMenuServicePrograms(
  items: readonly ReadOnlyMenuItem[],
): MenuServiceProgram[] {
  const programs = new Map<string, MenuServiceProgram>();

  for (const item of items) {
    if (!item.servicePolicy) continue;
    const existing = programs.get(item.servicePolicy.programId);
    if (existing) {
      existing.participantNames.push(item.name);
    } else {
      programs.set(item.servicePolicy.programId, {
        policy: item.servicePolicy,
        participantNames: [item.name],
      });
    }
  }

  return [...programs.values()];
}

function formatProgramParticipants(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

export function MenuServiceProgramNotice({ program }: { program: MenuServiceProgram }) {
  return (
    <aside
      data-menu-service-policy={program.policy.programId}
      className="rounded-lg border border-amber-500/30 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-950"
    >
      <p className="font-bold">{program.policy.label}</p>
      <p>{program.policy.note}</p>
      <p>{formatProgramParticipants(program.participantNames)}</p>
    </aside>
  );
}

function CategoryLink({
  section,
  activeId,
  onSelect,
  mobile = false,
  isRoma = false,
}: {
  section: ReadOnlyMenuSection;
  activeId: string;
  onSelect: (section: ReadOnlyMenuSection) => void;
  mobile?: boolean;
  isRoma?: boolean;
}) {
  const isActive = activeId === section.id;

  return (
    <a
      href={`#${section.id}`}
      aria-current={isActive ? "location" : undefined}
      data-mobile-category-id={mobile ? section.id : undefined}
      onClick={(event) => {
        event.preventDefault();
        onSelect(section);
      }}
      className={cn(
        "flex h-11 shrink-0 items-center rounded-[10px] border font-medium leading-none tracking-normal transition-[background-color,border-color,color,box-shadow,transform] duration-150 ease-out hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/15 active:translate-y-0 active:scale-[0.98] motion-reduce:transition-none motion-reduce:hover:translate-y-0",
        mobile
          ? "max-w-[78vw] snap-start px-3.5 text-[14px]"
          : "w-full px-4 text-[15px]",
        isActive
          ? `${ACTIVE_CATEGORY_TUSCAN} ${isRoma ? "roma-menu-active-category" : ""} shadow-[0_8px_20px_-16px_rgb(0_0_0_/_0.75)]`
          : INACTIVE_CATEGORY
      )}
    >
      <span className="truncate">{section.label}</span>
    </a>
  );
}

function PopularCard({
  item,
  onSelect,
  priority = false,
}: {
  item: PopularItem;
  onSelect: (item: ReadOnlyMenuItem) => void;
  priority?: boolean;
}) {
  return (
    <article
      data-analytics-menu-item={item.name}
      data-analytics-menu-category="Popular"
      data-analytics-menu-group="popular"
      data-analytics-has-image={item.image ? "true" : "false"}
      className="group relative w-[216px] shrink-0 snap-start rounded-[14px] transition-[transform,opacity] duration-150 ease-out hover:-translate-y-1 md:w-[248px] motion-reduce:transition-none motion-reduce:hover:translate-y-0"
    >
      <button
        type="button"
        onClick={() => onSelect(item)}
        aria-label={`View ${item.name}`}
        className="absolute inset-0 z-10 cursor-pointer rounded-[14px] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-[color:var(--roma-terracotta)]"
      />
      <div className="relative aspect-[4/5] overflow-hidden rounded-[12px] border border-[#e6e6e6]/70 bg-[#f0f0f0]">
        {item.image ? (
          <Image
            src={item.image}
            alt={item.name}
            fill
            priority={priority}
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : undefined}
            sizes="(min-width: 768px) 248px, 216px"
            className="object-cover transition-transform duration-300 ease-out group-hover:scale-[1.04] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-[#f6f6f6] text-sm font-medium text-[#707070]">
            {item.category ?? "Menu"}
          </div>
        )}
      </div>
      <h3 className="mt-3 line-clamp-2 text-[16px] font-bold leading-[1.24] tracking-normal text-[#0d0d0d] md:text-[17px]">
        {item.name}
      </h3>
      {item.price ? (
        <p className="mt-1 text-[16px] font-bold leading-6 tracking-normal text-[var(--roma-terracotta)] md:text-[17px]">
          {item.price}
        </p>
      ) : null}
    </article>
  );
}

function cleanWinePrice(value: string) {
  return value.replace(/\.00\b/g, "");
}

function getWinePriceChips(price: string) {
  if (!price) return [];

  if (price.includes("/")) {
    return price
      .split("/")
      .map((part) => cleanWinePrice(part.trim()))
      .filter(Boolean);
  }

  const priceText = cleanWinePrice(price.trim());

  return priceText ? [`Bottle ${priceText}`] : [];
}

function getWineDisplayParts(name: string) {
  const parts = name.split(",").map((part) => part.trim()).filter(Boolean);
  const wineStylePattern =
    /\b(barbera|blanc|cabernet|champagne|chardonnay|dark red|grigio|merlot|pinot|red blend|ros[eé]|sauvignon)\b/i;

  if (parts.length <= 1) {
    return { producer: name, varietal: "", region: "" };
  }

  if (parts.length === 2) {
    if (!wineStylePattern.test(parts[1])) {
      return {
        producer: parts[0],
        varietal: "",
        region: parts[1],
      };
    }

    return {
      producer: parts[0],
      varietal: parts[1],
      region: "",
    };
  }

  if (!wineStylePattern.test(parts[1])) {
    return {
      producer: parts[0],
      varietal: "",
      region: parts.slice(1).join(", "),
    };
  }

  return {
    producer: parts[0],
    varietal: parts[1],
    region: parts.slice(2).join(", "),
  };
}

function WineMenuItemCard({
  item,
  section,
  onSelect,
}: {
  item: ReadOnlyMenuItem;
  section: ReadOnlyMenuSection;
  onSelect: (item: ReadOnlyMenuItem) => void;
}) {
  const { producer, varietal, region } = getWineDisplayParts(item.name);
  const priceChips = getWinePriceChips(item.price);
  const isByTheGlass = item.price.includes("/");

  return (
    <article
      data-analytics-menu-item={item.name}
      data-analytics-menu-category={section.title}
      data-analytics-menu-group={section.id}
      data-analytics-has-image={item.image ? "true" : "false"}
      className="relative flex min-h-[128px] items-stretch justify-between gap-3 overflow-hidden rounded-[12px] border border-[#e6e6e6] bg-white px-4 py-4 shadow-[0_12px_30px_-28px_rgb(0_0_0_/_0.45)] transition-[border-color,box-shadow,transform] duration-150 ease-out md:min-h-0 md:hover:-translate-y-0.5 md:hover:border-[#d4d4d4] md:hover:shadow-[0_18px_38px_-30px_rgb(0_0_0_/_0.55)] motion-reduce:transition-none motion-reduce:hover:translate-y-0"
    >
      <button
        type="button"
        onClick={() => onSelect(item)}
        aria-label={`View ${item.name}`}
        className="absolute inset-0 z-10 cursor-pointer rounded-[12px] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-[color:var(--roma-terracotta)]"
      />
      <div className="flex min-w-0 flex-1 flex-col justify-center pr-1">
        <div className="space-y-1">
          <h3 className="text-[18px] font-bold leading-[1.18] tracking-normal text-[#0d0d0d] md:text-[17px]">
            {producer}
          </h3>
          {varietal ? (
            <p className="text-[15px] font-semibold leading-5 tracking-normal text-[#0d0d0d]">
              {varietal}
            </p>
          ) : null}
          {region ? (
            <p className="text-[13px] font-medium leading-5 tracking-normal text-[#5b5b5b]">
              {region}
            </p>
          ) : null}
        </div>
        {priceChips.length ? (
          <div
            className={cn(
              "mt-3 flex flex-wrap gap-1.5",
              isByTheGlass ? "max-w-[13rem]" : "max-w-[9rem]"
            )}
            aria-label={`Price for ${item.name}`}
          >
            {priceChips.map((price) => (
              <span
                key={price}
                className="inline-flex h-7 items-center rounded-full border border-[#e6e6e6] bg-white px-2.5 text-[13px] font-bold leading-none tracking-normal text-[var(--roma-terracotta)]"
              >
                {price}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      {item.image ? (
        <div className="relative my-auto h-[112px] w-[74px] shrink-0 md:h-[118px] md:w-[78px] lg:h-[132px] lg:w-[86px]">
          <Image
            src={item.image}
            alt={item.name}
            fill
            sizes="(min-width: 1024px) 86px, (min-width: 768px) 78px, 74px"
            className="object-contain drop-shadow-[0_10px_12px_rgb(0_0_0_/_0.14)]"
          />
        </div>
      ) : null}
    </article>
  );
}

function MenuItemCard({
  item,
  section,
  onSelect,
  photoGrid = false,
  compact = false,
}: {
  item: ReadOnlyMenuItem;
  section: ReadOnlyMenuSection;
  onSelect: (item: ReadOnlyMenuItem) => void;
  /**
   * Roma mobile treatment for fully-photographed sections: photo-on-top cards
   * in a 2-up grid below md, identical row cards from md up.
   */
  photoGrid?: boolean;
  /**
   * Roma mobile treatment for text-first groups (sides, drink lists): tight
   * name+price rows below md; full row cards return from md up.
   */
  compact?: boolean;
}) {
  const isWineItem = item.image?.includes("/images/menu/wine/") || section.id.includes("wine");
  const usesProductImageTreatment =
    item.image?.includes("/images/menu/beer/") ||
    item.image?.includes("/images/menu/wine/");

  // Compact mode intentionally bypasses the structured wine card so wine
  // sections read like the rest of the drink lists (Roma).
  if (isWineItem && !compact) {
    return <WineMenuItemCard item={item} section={section} onSelect={onSelect} />;
  }

  // Multi-part prices ("6oz $13 / 9oz $19 / Bottle $52") don't fit beside the
  // name in a compact row; stack them under it instead.
  const stackCompactPrice = compact && item.price.includes("/");

  return (
    <article
      data-analytics-menu-item={item.name}
      data-analytics-menu-category={section.title}
      data-analytics-menu-group={section.id}
      data-analytics-has-image={item.image ? "true" : "false"}
      className={cn(
        "relative flex overflow-hidden border-[#e6e6e6]/70 bg-white transition-[border-color,box-shadow,transform] duration-150 ease-out md:rounded-[12px] md:border md:border-solid md:hover:-translate-y-0.5 md:hover:border-[#c8c8c8] md:hover:shadow-[0_16px_36px_-30px_rgb(0_0_0_/_0.55)] motion-reduce:transition-none motion-reduce:hover:translate-y-0",
        usesProductImageTreatment ? "md:h-[8.25rem] lg:h-[9.5rem]" : "md:h-[9.5rem] lg:h-[12.5rem]",
        photoGrid
          ? "max-md:flex-col max-md:rounded-[14px] max-md:border max-md:border-solid max-md:shadow-[0_12px_30px_-26px_rgb(0_0_0_/_0.4)] md:min-h-[148px] md:flex-row md:justify-between md:gap-x-3"
          : cn(
              "justify-between gap-x-3 max-md:border-b",
              compact
                ? "max-md:py-3 md:min-h-[148px]"
                : "min-h-[148px] max-md:py-4",
              item.image ? "flex-row" : "flex-col md:flex-row"
            )
      )}
    >
      <button
        type="button"
        onClick={() => onSelect(item)}
        aria-label={`View ${item.name}`}
        className={cn(
          "absolute inset-0 z-10 cursor-pointer md:rounded-[12px] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-[color:var(--roma-terracotta)]",
          photoGrid && "max-md:rounded-[14px]"
        )}
      />
      <div
        className={cn(
          "flex min-w-0 flex-1 items-start py-0 md:px-4 md:py-4 lg:p-4",
          photoGrid && "max-md:px-3 max-md:py-3"
        )}
      >
        <div className={cn("flex min-w-0 flex-col gap-y-2 lg:gap-y-4", compact && "max-md:w-full")}>
          <div
            className={cn(
              "flex flex-col gap-y-1",
              compact &&
                !stackCompactPrice &&
                "max-md:flex-row max-md:items-baseline max-md:justify-between max-md:gap-x-3"
            )}
          >
            <h3
              className={cn(
                "line-clamp-2 text-base font-bold leading-[1.35] tracking-normal text-[#0d0d0d]",
                (photoGrid || compact) && "max-md:text-[15px] max-md:leading-[1.3]"
              )}
            >
              {item.name}
            </h3>
            {item.price ? (
              <p
                className={cn(
                  "text-base font-bold leading-5 tracking-normal text-[var(--roma-terracotta)]",
                  compact && "max-md:shrink-0 max-md:text-[15px]",
                  stackCompactPrice && "max-md:text-[13px] max-md:font-semibold"
                )}
              >
                {item.price}
              </p>
            ) : null}
          </div>
          {item.description ? (
            <p
              className={cn(
                "max-md:line-clamp-2 md:line-clamp-3 text-sm font-medium leading-5 tracking-normal text-[#4d4d4d] lg:line-clamp-4",
                (photoGrid || compact) && "max-md:hidden"
              )}
            >
              {item.description}
            </p>
          ) : null}
        </div>
      </div>

      {item.image ? (
        <div
          className={cn(
            "relative shrink-0 overflow-hidden md:h-full md:rounded-l-none md:rounded-r-[12px]",
            usesProductImageTreatment
              ? "h-[124px] w-[116px] rounded-[12px] bg-white md:w-[7.5rem] lg:w-[8rem]"
              : photoGrid
                ? "bg-[#f0f0f0] max-md:order-first max-md:aspect-[4/3] max-md:w-full max-md:rounded-none md:aspect-square"
                : "aspect-square h-[124px] rounded-[12px] bg-[#f0f0f0]",
            compact && "max-md:hidden"
          )}
        >
          <Image
            src={item.image}
            alt={item.name}
            fill
            sizes={
              usesProductImageTreatment
                ? "(min-width: 1024px) 128px, (min-width: 768px) 120px, 116px"
                : photoGrid
                  ? "(min-width: 768px) 200px, 46vw"
                  : "(min-width: 768px) 200px, 124px"
            }
            className={cn(
              usesProductImageTreatment ? "object-contain p-2.5 md:p-3" : "object-cover"
            )}
          />
        </div>
      ) : null}
    </article>
  );
}

function parsePriceValue(price: string): number {
  const match = price.match(/\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : 0;
}

/**
 * Full-width premium feature card (Roma): the section's highest-priced
 * photographed dish gets the anchor slot at the top of its section.
 */
function FeaturedMenuItemCard({
  item,
  section,
  onSelect,
}: {
  item: ReadOnlyMenuItem;
  section: ReadOnlyMenuSection;
  onSelect: (item: ReadOnlyMenuItem) => void;
}) {
  return (
    <article
      data-analytics-menu-item={item.name}
      data-analytics-menu-category={section.title}
      data-analytics-menu-group={section.id}
      data-analytics-has-image="true"
      className="relative flex flex-col overflow-hidden rounded-[14px] border border-[#e6e6e6]/70 bg-white shadow-[0_16px_40px_-30px_rgb(0_0_0_/_0.5)] transition-[border-color,box-shadow,transform] duration-150 ease-out md:hover:-translate-y-0.5 md:hover:border-[#c8c8c8] motion-reduce:transition-none motion-reduce:hover:translate-y-0"
    >
      <button
        type="button"
        onClick={() => onSelect(item)}
        aria-label={`View ${item.name}`}
        className="absolute inset-0 z-10 cursor-pointer rounded-[14px] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-[color:var(--roma-terracotta)]"
      />
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-[#f0f0f0] md:aspect-[16/9]">
        <Image
          src={item.image!}
          alt={item.name}
          fill
          sizes="(min-width: 1280px) 940px, (min-width: 1024px) calc(100vw - 340px), calc(100vw - 2rem)"
          className="object-cover object-[50%_30%]"
        />
      </div>
      <div className="flex flex-col gap-y-1.5 px-4 py-4 md:px-5">
        <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#9a6d2f] dark:text-[var(--roma-star)]">
          Chef&apos;s feature
        </p>
        <div className="flex items-baseline justify-between gap-x-3">
          <h3 className="text-[18px] font-bold leading-[1.25] tracking-normal text-[#0d0d0d] md:text-[20px]">
            {item.name}
          </h3>
          {item.price ? (
            <p className="shrink-0 text-[17px] font-bold leading-6 tracking-normal text-[var(--roma-terracotta)] md:text-[18px]">
              {item.price}
            </p>
          ) : null}
        </div>
        {item.description ? (
          <p className="line-clamp-2 text-sm font-medium leading-5 tracking-normal text-[#4d4d4d]">
            {item.description}
          </p>
        ) : null}
      </div>
    </article>
  );
}

function SectionGuideLink({ sectionId }: { sectionId: string }) {
  const link = sectionGuideLinks[sectionId];

  if (!link) return null;

  return (
    <Link
      href={link.href}
      prefetch={false}
      className="text-sm font-bold leading-5 tracking-normal text-[#0d0d0d] underline-offset-4 hover:underline"
    >
      {link.label}
    </Link>
  );
}

// Memoized: the scrollspy updates `activeId` state on every section change
// while scrolling; without memo that re-renders every card on the page.
const MenuSectionBlock = memo(function MenuSectionBlock({
  section,
  items,
  onSelect,
  isRoma = false,
}: {
  section: ReadOnlyMenuSection;
  items: ReadOnlyMenuItem[];
  onSelect: (item: ReadOnlyMenuItem) => void;
  isRoma?: boolean;
}) {
  if (!items.length) return null;

  const servicePrograms = groupMenuServicePrograms(items);

  // Roma premium anchor: the priciest photographed dish in the mains section
  // leads it as a full-width feature (price anchoring for the section below).
  const featuredItem =
    isRoma && section.id === "menu-secondi-primi"
      ? items.reduce<ReadOnlyMenuItem | null>((best, item) => {
          if (!item.image) return best;
          if (!best) return item;
          return parsePriceValue(item.price) > parsePriceValue(best.price)
            ? item
            : best;
        }, null)
      : null;
  const listItems = featuredItem
    ? items.filter((item) => item.id !== featuredItem.id)
    : items;

  // Roma's Convex sort order interleaves subcategories (for example Mains,
  // Seafood, then Mains again). Intentionally aggregate Roma items into one
  // stable block per category, preserving order inside each block, so its
  // headings never repeat. Other tenants retain their full source ordering
  // because this is a shared menu component.
  const groups: Array<{ title?: string; items: ReadOnlyMenuItem[] }> = [];
  if (isRoma) {
    const groupsByTitle = new Map<string, (typeof groups)[number]>();

    for (const item of listItems) {
      const title = item.subcategory?.trim() || undefined;
      const key = title?.toLowerCase() ?? "__uncategorized__";
      const existing = groupsByTitle.get(key);

      if (existing) {
        existing.items.push(item);
        continue;
      }

      const group = { title, items: [item] };
      groupsByTitle.set(key, group);
      groups.push(group);
    }
  } else {
    for (const item of listItems) {
      const title = item.subcategory?.trim() || undefined;
      const current = groups[groups.length - 1];

      if (current?.title === title) {
        current.items.push(item);
      } else {
        groups.push({ title, items: [item] });
      }
    }
  }

  // A section with one named group already has an H2 carrying the category;
  // repeating the same idea as a lone H3 adds noise without helping scanning.
  const showSubsections =
    groups.some((group) => group.title) && (!isRoma || groups.length > 1);

  return (
    <section
      id={section.id}
      data-menu-section={section.id}
      data-analytics-menu-category={section.title}
      className="scroll-mt-[11rem]"
    >
      {section.anchors?.map((anchor) => (
        <span key={anchor} id={anchor} className="block scroll-mt-[11rem]" aria-hidden="true" />
      ))}
      <div className="mb-4 flex flex-col gap-2 md:mb-5">
        <h2 className="text-[22px] font-bold leading-7 tracking-normal text-[#0d0d0d]">
          {section.title}
        </h2>
        {section.note ? (
          <p className="max-w-3xl text-sm font-medium leading-5 tracking-normal text-[#4d4d4d]">
            {section.note}
          </p>
        ) : null}
        <SectionGuideLink sectionId={section.id} />
      </div>
      {featuredItem ? (
        <div className="mb-6">
          <FeaturedMenuItemCard
            item={featuredItem}
            section={section}
            onSelect={onSelect}
          />
        </div>
      ) : null}
      {servicePrograms.length ? (
        <div className="mb-6 space-y-2">
          {servicePrograms.map((program) => (
            <MenuServiceProgramNotice key={program.policy.programId} program={program} />
          ))}
        </div>
      ) : null}
      <div className="space-y-7">
        {groups.map((group, index) => {
          // Roma mobile: fully-photographed groups render as a 2-up photo
          // gallery grid below md instead of the text-first list rows.
          const usePhotoGrid =
            isRoma &&
            group.items.length >= 2 &&
            group.items.every(
              (item) =>
                item.image &&
                !item.image.includes("/images/menu/beer/") &&
                !item.image.includes("/images/menu/wine/")
            );
          // Roma: text-first groups tighten into compact name+price rows
          // (photos and descriptions return at md). Zero-photo groups of any
          // size qualify, which folds wine sections into the same list look
          // as the other drink lists instead of the structured wine card.
          const photoCount = group.items.filter((item) => item.image).length;
          const useCompactRows =
            isRoma &&
            !usePhotoGrid &&
            (photoCount === 0 ||
              (group.items.length >= 4 && photoCount < group.items.length / 2));

          return (
            <div key={`${group.title ?? "menu"}-${index}`}>
              {showSubsections && group.title ? (
                <h3 className="mb-3 text-sm font-bold uppercase leading-5 tracking-normal text-[var(--roma-gold-ink)]">
                  {group.title}
                </h3>
              ) : null}
              <div
                className={cn(
                  usePhotoGrid
                    ? "grid grid-cols-2 gap-3 md:grid-cols-2 md:gap-2 lg:gap-4"
                    : "grid grid-cols-1 md:grid-cols-2 md:gap-2 lg:gap-4",
                  section.id.includes("wine")
                    ? useCompactRows
                      ? "md:auto-rows-[8.5rem] lg:auto-rows-[9.5rem]"
                      : "gap-3 md:auto-rows-[8.5rem] lg:auto-rows-[9.5rem]"
                    : null,
                  group.items.every(
                    (item) =>
                      item.image?.includes("/images/menu/beer/") ||
                      item.image?.includes("/images/menu/wine/")
                  ) && !section.id.includes("wine")
                    ? "md:auto-rows-[8.25rem] lg:auto-rows-[9.5rem]"
                    : !section.id.includes("wine")
                      ? "md:auto-rows-[9.5rem] lg:auto-rows-[12.5rem]"
                      : null
                )}
              >
                {group.items.map((item) => (
                  <MenuItemCard
                    key={item.id}
                    item={item}
                    section={section}
                    onSelect={onSelect}
                    photoGrid={usePhotoGrid}
                    compact={useCompactRows}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
});

function MenuHeader({
  location,
  copy,
}: Pick<ReadOnlyOwnerMenuProps, "location"> & {
  copy: ResolvedReadOnlyOwnerMenuCopy;
}) {
  const phoneHref = `tel:${location.phone.replace(/[^\d+]/g, "")}`;
  const secondaryPhoneHref = location.secondary
    ? `tel:${location.secondary.phone.replace(/[^\d+]/g, "")}`
    : null;

  return (
    <section className="flex flex-col gap-5">
      <div>
        <h1 className="text-[22px] font-bold leading-7 tracking-normal text-[#0d0d0d]">
          {copy.title}
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm font-medium text-[#4d4d4d]">
          {location.label ? (
            <span className="font-bold text-[#0d0d0d]">{location.label}:</span>
          ) : null}
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-4 w-4 fill-[#4d4d4d] stroke-[#4d4d4d]" />
            {location.hours}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <MapPin className="h-4 w-4" />
            {location.neighborhood}
          </span>
          <a className="inline-flex items-center gap-1.5 underline-offset-4 hover:underline" href={phoneHref}>
            <Phone className="h-4 w-4" />
            {location.phone}
          </a>
        </div>
        {location.secondary && secondaryPhoneHref ? (
          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm font-medium text-[#4d4d4d]">
            <span className="font-bold text-[#0d0d0d]">{location.secondary.label}:</span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-4 w-4 fill-[#4d4d4d] stroke-[#4d4d4d]" />
              {location.secondary.hours}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-4 w-4" />
              {location.secondary.neighborhood}
            </span>
            <a
              className="inline-flex items-center gap-1.5 underline-offset-4 hover:underline"
              href={secondaryPhoneHref}
            >
              <Phone className="h-4 w-4" />
              {location.secondary.phone}
            </a>
          </div>
        ) : null}
        {copy.hoursHighlight ? (
          // Message match for paid-social landings: echo the ad's hours promise
          // right under the hours row. Bronze on light, gold on the dark funnel
          // (html.dark exists exactly when the funnel is on).
          <p className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[#9a6d2f]/35 px-3 py-1.5 text-[13px] font-bold leading-none text-[#9a6d2f] dark:border-[var(--roma-star)]/40 dark:text-[var(--roma-star)]">
            <Clock className="h-3.5 w-3.5" aria-hidden="true" />
            {copy.hoursHighlight}
          </p>
        ) : null}
        <p className="mt-4 max-w-3xl text-[15px] font-medium leading-6 tracking-normal text-[#4d4d4d]">
          {copy.description}
        </p>
      </div>
    </section>
  );
}

function MenuGuideLinks({ copy }: { copy: ResolvedReadOnlyOwnerMenuCopy }) {
  if (!copy.menuGuideLinks.length) return null;

  return (
    <section className="rounded-[12px] border border-[#e6e6e6] bg-white p-4" aria-labelledby="menu-guides-heading">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.08em] text-[#707070]">
            Menu guides
          </p>
          <h2 id="menu-guides-heading" className="mt-1 text-[20px] font-bold leading-7 tracking-normal text-[#0d0d0d]">
            Plan by occasion
          </h2>
        </div>
        <p className="max-w-md text-sm font-medium leading-5 tracking-normal text-[#4d4d4d]">
          {copy.guideIntro}
        </p>
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {copy.menuGuideLinks.map((link) => (
          <Link
            key={link.href}
            href={link.href}
            prefetch={false}
            aria-label={link.ariaLabel}
            className="group min-h-[6.75rem] rounded-[10px] border border-[#e6e6e6] bg-[#f8f8f8] px-4 py-3 transition-colors hover:border-[#bcbcbc] hover:bg-white"
          >
            <span className="block text-[15px] font-bold leading-5 tracking-normal text-[#0d0d0d] group-hover:underline group-hover:underline-offset-4">
              {link.label}
            </span>
            <span className="mt-2 block text-sm font-medium leading-5 tracking-normal text-[#4d4d4d]">
              {link.description}
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function MenuAtAGlance({ copy }: { copy: ResolvedReadOnlyOwnerMenuCopy }) {
  if (!copy.atAGlanceItems.length) return null;

  return (
    <section
      className="rounded-[12px] border border-[#e6e6e6] bg-white p-4"
      aria-labelledby="menu-at-a-glance-heading"
    >
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.08em] text-[#707070]">
            Menu at a glance
          </p>
          <h2
            id="menu-at-a-glance-heading"
            className="mt-1 text-[20px] font-bold leading-7 tracking-normal text-[#0d0d0d]"
          >
            {copy.atAGlanceTitle}
          </h2>
        </div>
        <p className="max-w-md text-sm font-medium leading-5 tracking-normal text-[#4d4d4d]">
          {copy.atAGlanceIntro}
        </p>
      </div>
      <dl className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {copy.atAGlanceItems.map((item) => (
          <div key={item.label} className="rounded-[10px] border border-[#e6e6e6] bg-[#f8f8f8] p-4">
            <dt className="text-[15px] font-bold leading-5 tracking-normal text-[#0d0d0d]">
              {item.label}
            </dt>
            <dd className="mt-2 text-sm font-medium leading-5 tracking-normal text-[#4d4d4d]">
              {item.detail}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function MenuSeoHighlights() {
  return (
    <section className="grid gap-3 md:grid-cols-3" aria-labelledby="menu-seo-highlights-heading">
      <div className="md:col-span-3">
        <p className="text-xs font-bold uppercase tracking-[0.08em] text-[#707070]">
          Menu details
        </p>
        <h2 id="menu-seo-highlights-heading" className="mt-1 text-[20px] font-bold leading-7 tracking-normal text-[#0d0d0d]">
          What the Tuscan Cove Menu Covers
        </h2>
      </div>
      {menuSeoHighlights.map((item) => (
        <article key={item.title} className="rounded-[12px] border border-[#e6e6e6] bg-[#f8f8f8] p-4">
          <h3 className="text-[15px] font-bold leading-5 tracking-normal text-[#0d0d0d]">
            {item.title}
          </h3>
          <p className="mt-2 text-sm font-medium leading-6 tracking-normal text-[#4d4d4d]">
            {item.body}
          </p>
        </article>
      ))}
    </section>
  );
}

function MenuSeoFaqs() {
  return (
    <section className="rounded-[12px] border border-[#e6e6e6] bg-white p-4" aria-labelledby="menu-faq-heading">
      <p className="text-xs font-bold uppercase tracking-[0.08em] text-[#707070]">
        Menu FAQ
      </p>
      <h2 id="menu-faq-heading" className="mt-1 text-[20px] font-bold leading-7 tracking-normal text-[#0d0d0d]">
        Common Menu Questions
      </h2>
      <div className="mt-4 divide-y divide-[#ededed]">
        {menuSeoFaqs.map((faq) => (
          <article key={faq.question} className="py-4 first:pt-0 last:pb-0">
            <h3 className="text-[15px] font-bold leading-5 tracking-normal text-[#0d0d0d]">
              {faq.question}
            </h3>
            <p className="mt-2 text-sm font-medium leading-6 tracking-normal text-[#4d4d4d]">
              {faq.answer}
            </p>
          </article>
        ))}
      </div>
    </section>
  );
}

function MenuItemDialog({
  item,
  onClose,
}: {
  item: ReadOnlyMenuItem | null;
  onClose: () => void;
}) {
  // The dialog is portaled to document.body (outside the public layout's
  // data-public-page-brand wrapper), so the brand palette would fall back to the
  // muted :root accent. Re-apply the resolved brand on the portaled content so
  // its accents (the orange Reserve CTA + eyebrow) render the brand orange.
  // The funnel attribute must ride along too: brand-without-funnel matches the
  // Roma LIGHT token block, which would repaint the portaled dialog cream
  // while the page behind it is dark.
  const { siteConfig } = useMarketingLocationContext();
  const pageBrand = getPublicPageBrand(siteConfig);
  const romaFunnelAttribute = shouldApplyRomaDarkFunnel(pageBrand)
    ? "on"
    : undefined;
  return (
    <Dialog
      open={Boolean(item)}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      {item ? (
        <DialogContent
          showCloseButton={false}
          data-public-page-brand={pageBrand}
          data-roma-funnel={romaFunnelAttribute}
          // The dialog portals to document.body, outside the /menu dark-subpage
          // scrub, so it must paint itself from semantic tokens (they resolve
          // dark on the Roma funnel via the html-level token block and stay
          // light everywhere else).
          className="flex max-h-[88dvh] flex-col gap-0 overflow-hidden rounded-[18px] border-none bg-[var(--card)] p-0 shadow-[0_40px_80px_-32px_rgb(0_0_0_/_0.55)] sm:max-w-[420px]"
        >
          {item.image ? (
            <div className="relative aspect-[4/3] w-full shrink-0 bg-[var(--muted)]">
              <Image
                src={item.image}
                alt={item.name}
                fill
                sizes="(min-width: 640px) 420px, calc(100vw - 2rem)"
                className="object-cover object-center"
              />
            </div>
          ) : null}

          <DialogClose
            aria-label="Close"
            className="absolute right-3 top-3 z-20 inline-flex h-9 w-9 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--card)] text-[var(--card-foreground)] shadow-[0_6px_18px_-8px_rgb(0_0_0_/_0.55)] backdrop-blur transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-[color:var(--roma-terracotta)]"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </DialogClose>

          <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 py-5 sm:px-6 sm:py-6">
            <DialogTitle className="pr-10 text-[24px] font-bold leading-[1.18] tracking-normal text-[var(--card-foreground)]">
              {item.name}
            </DialogTitle>
            {item.price ? (
              <p className="mt-1.5 text-[17px] font-bold leading-6 tracking-normal text-[var(--card-foreground)]">
                {item.price}
              </p>
            ) : null}
            {item.description ? (
              <DialogDescription className="mt-3 text-[15px] font-medium leading-6 tracking-normal text-[var(--muted-foreground)]">
                {item.description}
              </DialogDescription>
            ) : (
              <DialogDescription className="sr-only">
                {`${item.name} menu item details`}
              </DialogDescription>
            )}

            {pageBrand === "chefs-roma" ? (
              <MenuItemCtaFooter itemName={item.name} />
            ) : (
              <MenuReserveFooter itemName={item.name} />
            )}
          </div>
        </DialogContent>
      ) : null}
    </Dialog>
  );
}

export function ReadOnlyOwnerMenu({
  sections,
  popularItems,
  location,
  copy,
}: ReadOnlyOwnerMenuProps) {
  const { siteConfig } = useMarketingLocationContext();
  const pageBrand = getPublicPageBrand(siteConfig);
  const isRoma = pageBrand === "chefs-roma";
  // The quick-action bar belongs to the dark-funnel experience; a light-mode
  // rollback (funnel flag off) also rolls the bar back.
  const showRomaMenuBar = isRoma && shouldApplyRomaDarkFunnel(pageBrand);
  // WebSite JSON-LD SearchAction (src/lib/seo.ts) advertises
  // /menu?search={search_term_string} as the site searchbox target.
  // Read the query here so the URL stays live and shareable. Cap at
  // 80 chars defensively.
  const searchParams = useSearchParams();
  const initialQuery = (searchParams?.get("search") ?? "").slice(0, 80);
  const [query, setQuery] = useState(initialQuery);
  const [activeId, setActiveId] = useState(sections[0]?.id ?? "");
  // True while a chip-tap smooth scroll is in flight; the scrollspy stays
  // quiet so the active chip doesn't flicker through intermediate sections.
  const scrollSpySuppressedRef = useRef(false);
  const scrollSpyReleaseTimeoutRef = useRef<number | null>(null);
  const scrollSpyReleaseHandlerRef = useRef<(() => void) | null>(null);
  const cancelPendingScrollSpyRelease = useCallback(() => {
    if (scrollSpyReleaseTimeoutRef.current !== null) {
      window.clearTimeout(scrollSpyReleaseTimeoutRef.current);
      scrollSpyReleaseTimeoutRef.current = null;
    }
    if (scrollSpyReleaseHandlerRef.current) {
      window.removeEventListener(
        "scrollend",
        scrollSpyReleaseHandlerRef.current,
      );
      scrollSpyReleaseHandlerRef.current = null;
    }
  }, []);

  // Flat id -> item lookup so the modal can resolve a shared/deep-linked
  // ?item= value (including popular-carousel-only items) and the popstate
  // handler can reopen the right card on back/forward navigation.
  const itemsById = useMemo(() => {
    const map = new Map<string, ReadOnlyMenuItem>();
    for (const section of sections) {
      for (const item of section.items) {
        map.set(item.id, item);
      }
    }
    for (const item of popularItems) {
      if (!map.has(item.id)) {
        map.set(item.id, item);
      }
    }
    return map;
  }, [sections, popularItems]);

  const [activeItem, setActiveItem] = useState<ReadOnlyMenuItem | null>(() => {
    const itemId = searchParams?.get("item");
    return itemId ? itemsById.get(itemId) ?? null : null;
  });

  const openItem = useCallback((item: ReadOnlyMenuItem) => {
    setActiveItem(item);
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    params.set("item", item.id);
    // pushState (not replaceState) so the browser Back button closes the
    // modal, mirroring the Owner.com reference's ?item= deep links.
    window.history.pushState(
      { menuItem: item.id },
      "",
      `${window.location.pathname}?${params.toString()}${window.location.hash}`
    );
  }, []);

  const closeItem = useCallback(() => {
    setActiveItem(null);
    if (typeof window === "undefined") return;
    const state = window.history.state as { menuItem?: string } | null;
    if (state?.menuItem) {
      // Pop the entry we pushed on open so forward/back history stays clean.
      window.history.back();
      return;
    }
    // Direct deep-link (no pushed entry): just strip ?item= in place.
    const params = new URLSearchParams(window.location.search);
    params.delete("item");
    const nextSearch = params.toString();
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash}`
    );
  }, []);

  const normalizedQuery = normalizeSearch(query);
  const deferredQuery = useDeferredValue(normalizedQuery);
  const resolvedCopy: ResolvedReadOnlyOwnerMenuCopy = {
    ...defaultMenuCopy,
    ...copy,
    menuGuideLinks: copy?.menuGuideLinks ?? defaultMenuCopy.menuGuideLinks,
    atAGlanceItems: copy?.atAGlanceItems ?? defaultMenuCopy.atAGlanceItems,
    showSeoSections: copy?.showSeoSections ?? defaultMenuCopy.showSeoSections,
  };

  const filteredSections = useMemo(
    () =>
      sections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) => itemMatchesSearch(item, deferredQuery)),
        }))
        .filter((section) => section.items.length > 0),
    [deferredQuery, sections]
  );

  const filteredPopularItems = useMemo(
    () => popularItems.filter((item) => itemMatchesSearch(item, deferredQuery)),
    [deferredQuery, popularItems]
  );

  const selectSection = useCallback((section: ReadOnlyMenuSection) => {
    const target = document.getElementById(section.id);
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    setActiveId(section.id);
    cancelPendingScrollSpyRelease();
    scrollSpySuppressedRef.current = true;
    const releaseSpy = () => {
      if (scrollSpyReleaseHandlerRef.current !== releaseSpy) return;
      cancelPendingScrollSpyRelease();
      scrollSpySuppressedRef.current = false;
    };
    scrollSpyReleaseHandlerRef.current = releaseSpy;
    // scrollend ends suppression as soon as the scroll settles; the timeout
    // covers browsers without scrollend and interrupted scrolls.
    scrollSpyReleaseTimeoutRef.current = window.setTimeout(
      releaseSpy,
      prefersReducedMotion ? 300 : 1500,
    );
    window.addEventListener("scrollend", releaseSpy);
    // Preserve any existing history.state (e.g. the modal's {menuItem} marker)
    // so closeItem can still consume its pushed entry via history.back().
    window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}#${section.id}`);
    target?.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "start",
    });
  }, [cancelPendingScrollSpyRelease]);

  useEffect(
    () => () => {
      cancelPendingScrollSpyRelease();
      scrollSpySuppressedRef.current = false;
    },
    [cancelPendingScrollSpyRelease],
  );

  useEffect(() => {
    const queryParam = new URLSearchParams(window.location.search).get("search");
    if (queryParam) {
      setQuery(queryParam.slice(0, 80));
    }
  }, []);

  useEffect(() => {
    const syncSearchParam = window.setTimeout(() => {
      const params = new URLSearchParams(window.location.search);
      const trimmedQuery = query.trim().slice(0, 80);

      if (trimmedQuery) {
        params.set("search", trimmedQuery);
      } else {
        params.delete("search");
      }

      const nextSearch = params.toString();
      const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}${window.location.hash}`;

      if (nextUrl !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
        // Keep history.state intact so a modal opened mid-search keeps its
        // {menuItem} marker and closes cleanly via history.back().
        window.history.replaceState(window.history.state, "", nextUrl);
      }
    }, 120);

    return () => window.clearTimeout(syncSearchParam);
  }, [query]);

  useEffect(() => {
    const sectionElements = filteredSections
      .map((section) => document.getElementById(section.id))
      .filter((element): element is HTMLElement => Boolean(element));
    if (!sectionElements.length) return;

    let animationFrame = 0;
    const resolveReadingLine = () => {
      const rootScrollPaddingTop = Number.parseFloat(
        window.getComputedStyle(document.documentElement).scrollPaddingTop,
      );
      const sectionScrollMarginTop = Number.parseFloat(
        window.getComputedStyle(sectionElements[0]).scrollMarginTop,
      );

      return getMenuScrollSpyReadingLine({
        viewportHeight: window.innerHeight,
        rootScrollPaddingTop,
        sectionScrollMarginTop,
      });
    };
    let readingLine = resolveReadingLine();

    const updateActiveSection = () => {
      animationFrame = 0;
      if (scrollSpySuppressedRef.current) return;

      // Choose the last section whose top has crossed a stable reading line.
      // This works for both very tall and very short sections; intersection
      // ratios caused tall Roma sections to skip their category chip entirely.
      let nextId = sectionElements[0]?.id ?? "";

      for (const element of sectionElements) {
        if (element.getBoundingClientRect().top > readingLine) break;
        nextId = element.id;
      }

      const atPageBottom =
        window.scrollY + window.innerHeight >=
        document.documentElement.scrollHeight - 2;
      if (atPageBottom) {
        nextId = sectionElements[sectionElements.length - 1]?.id ?? nextId;
      }

      if (nextId) {
        setActiveId((currentId) => (currentId === nextId ? currentId : nextId));
      }
    };

    const scheduleUpdate = () => {
      if (animationFrame) return;
      animationFrame = window.requestAnimationFrame(updateActiveSection);
    };
    const handleResize = () => {
      readingLine = resolveReadingLine();
      scheduleUpdate();
    };

    updateActiveSection();
    window.addEventListener("scroll", scheduleUpdate, { passive: true });
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", scheduleUpdate);
      window.removeEventListener("resize", handleResize);
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
    };
  }, [filteredSections]);

  useEffect(() => {
    const applyHashSection = () => {
      const hash = decodeURIComponent(window.location.hash.replace(/^#/, ""));
      if (!hash) return;

      const hashSection = sections.find(
        (section) => section.id === hash || section.anchors?.includes(hash)
      );

      if (hashSection) {
        setActiveId(hashSection.id);
      }
    };

    applyHashSection();
    window.addEventListener("hashchange", applyHashSection);

    return () => window.removeEventListener("hashchange", applyHashSection);
  }, [sections]);

  useEffect(() => {
    const mobileLink = document.querySelector<HTMLElement>(
      `[data-mobile-category-id="${activeId}"]`
    );
    const mobileCategoryScroller = mobileLink?.parentElement;
    if (!mobileLink || !mobileCategoryScroller) return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const mobileLinkRect = mobileLink.getBoundingClientRect();
    const mobileCategoryScrollerRect = mobileCategoryScroller.getBoundingClientRect();

    mobileCategoryScroller.scrollTo({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      left: getCenteredHorizontalScrollLeft({
        currentScrollLeft: mobileCategoryScroller.scrollLeft,
        itemLeft: mobileLinkRect.left,
        itemWidth: mobileLinkRect.width,
        containerLeft: mobileCategoryScrollerRect.left,
        containerWidth: mobileCategoryScroller.clientWidth,
      }),
    });
  }, [activeId]);

  useEffect(() => {
    if (!filteredSections.some((section) => section.id === activeId)) {
      setActiveId(filteredSections[0]?.id ?? sections[0]?.id ?? "");
    }
  }, [activeId, filteredSections, sections]);

  // Keep the open modal in sync with browser Back/Forward by reading ?item=.
  useEffect(() => {
    const handlePopState = () => {
      const itemId = new URLSearchParams(window.location.search).get("item");
      setActiveItem(itemId ? itemsById.get(itemId) ?? null : null);
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [itemsById]);

  return (
    <div
      className={cn(
        "mx-auto grid max-w-7xl gap-8 px-4 py-6 md:px-6 lg:grid-cols-[256px_minmax(0,1fr)] lg:gap-9",
        // Reserve the Roma mobile quick-action bar's real height (plus the
        // home-indicator inset) on top of the normal 1.5rem bottom padding,
        // so the last menu section clears the bar before it hides.
        showRomaMenuBar &&
          "max-md:pb-[calc(var(--roma-menu-bar-height)+env(safe-area-inset-bottom,0px)+1.5rem)]"
      )}
    >
      <aside className="hidden lg:block">
        <div className="sticky top-[calc(var(--site-header-height)+0.5rem)] max-h-[calc(100dvh-120px)] overflow-y-auto pr-1">
          <label className="relative mb-2 block">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#4d4d4d]" />
            <span className="sr-only">Search menu</span>
            <input
              type="search"
              name="menu-search"
              autoComplete="off"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search menu"
              className="h-12 w-full rounded-[10px] border border-[#e6e6e6] bg-white pl-12 pr-4 text-base font-medium text-[#4d4d4d] shadow-none outline-none transition-[border-color,box-shadow] placeholder:text-[#707070] focus-visible:border-[var(--roma-terracotta)] focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/15"
            />
          </label>
          <nav aria-label="Menu categories" className="flex flex-col gap-1.5">
            {sections.map((section) => (
              <CategoryLink
                key={section.id}
                section={section}
                activeId={activeId}
                onSelect={selectSection}
                isRoma={isRoma}
              />
            ))}
          </nav>
        </div>
      </aside>

      <div className="min-w-0 space-y-10 md:space-y-12">
        <MenuHeader location={location} copy={resolvedCopy} />

        {filteredPopularItems.length ? (
          <section className="space-y-5" aria-labelledby="popular-menu-heading">
            <h2 id="popular-menu-heading" className="text-[22px] font-bold leading-7 tracking-normal text-[#0d0d0d]">
              Popular
            </h2>
            <div className="-mx-4 snap-x snap-proximity scroll-px-4 overflow-x-auto px-4 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="flex gap-3 pr-4 sm:gap-4">
                {filteredPopularItems.map((item, index) => (
                  <PopularCard
                    key={item.id}
                    item={item}
                    onSelect={openItem}
                    priority={index === 0}
                  />
                ))}
              </div>
            </div>
          </section>
        ) : null}

        <div className="sticky top-[var(--site-header-height)] z-20 -mx-4 border-y border-[#eeeeee] bg-white px-4 py-3 shadow-[0_10px_24px_-22px_rgb(0_0_0_/_0.42)] lg:hidden">
          <div className="flex snap-x snap-proximity gap-2 overflow-x-auto overscroll-x-contain scroll-px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <label className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] border border-[#e6e6e6] bg-white text-[#0d0d0d]">
              <Search className="h-5 w-5" />
              <span className="sr-only">Search menu</span>
              <input
                type="search"
                name="mobile-menu-search"
                autoComplete="off"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search menu"
                className="absolute inset-0 h-full w-full cursor-text rounded-[10px] opacity-0 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/15"
              />
            </label>
            {sections.map((section) => (
              <CategoryLink
                key={section.id}
                section={section}
                activeId={activeId}
                onSelect={selectSection}
                mobile
                isRoma={isRoma}
              />
            ))}
          </div>
          {query ? (
            <div className="mt-2 flex items-center justify-between gap-3 text-xs font-medium text-[#707070]">
              <span className="truncate">Filtering for “{query}”</span>
              <button
                type="button"
                className="shrink-0 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[color:var(--roma-terracotta)]/15"
                onClick={() => setQuery("")}
              >
                Clear
              </button>
            </div>
          ) : null}
        </div>

        {filteredSections.length ? (
          <div className="space-y-10 transition-opacity duration-150 ease-out md:space-y-12">
            {filteredSections.map((section) => (
              <MenuSectionBlock
                key={section.id}
                section={section}
                items={section.items}
                onSelect={openItem}
                isRoma={isRoma}
              />
            ))}
          </div>
        ) : (
          <section className="rounded-[12px] border border-[#e6e6e6] bg-white p-8 text-center">
            <h2 className="text-xl font-bold tracking-normal text-[#0d0d0d]">No menu items found</h2>
            <p className="mt-2 text-sm font-medium leading-5 text-[#4d4d4d]">
              {resolvedCopy.emptyStateDescription}
            </p>
            <button
              type="button"
              className={cn(
                "mt-5 h-10 rounded-[10px] bg-[#f45c24] px-4 text-sm font-bold text-[#1a1a1a] transition-transform hover:bg-[#d94e1a] active:scale-[0.98]",
                isRoma && "roma-menu-cta"
              )}
              onClick={() => setQuery("")}
            >
              Clear search
            </button>
          </section>
        )}

        <MenuAtAGlance copy={resolvedCopy} />

        <MenuGuideLinks copy={resolvedCopy} />

        {resolvedCopy.showSeoSections ? (
          <>
            <MenuSeoHighlights />

            <MenuSeoFaqs />
          </>
        ) : null}
      </div>

      <MenuItemDialog item={activeItem} onClose={closeItem} />

      {/* Keep the fixed bar out of the `space-y-*` column: there it inherited
          a 40px margin-bottom that lifted the `bottom-0` bar off the viewport
          edge, so menu content showed through underneath it. */}
      {showRomaMenuBar ? <RomaMenuMobileCtaBar /> : null}
    </div>
  );
}
